import React, { ReactNode, useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { ugcDisplayRulesPath } from '@/common/app/path'
import { UGCDisplayRule } from '@/common/model/ugcDisplay'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import Heading from '@/components/view/atoms/heading/Heading'
import Area from '@/components/view/layouts/area/Area'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import { fetchRest } from '@/common/api/fetcher'

const INPUT = 'h-8 rounded-lg border border-border-input bg-surface px-2.5 text-size-13 text-primary outline-none hover:border-border-input-hover focus:border-border-input-active placeholder:text-placeholder'
// 幅は「入る値の長さ」に合わせる。すべて同じ幅だと、どこに何を書くのか読み取れない
const W_NUM = 'w-24'
const W_SHORT = 'w-40'
const W_MID = 'w-60'
const W_LONG = 'w-full max-w-[28rem]'

const LABEL = 'text-primary text-[12px] font-semibold'
const HINT = 'text-tertiary text-[11px] leading-relaxed'
const ROW = 'flex flex-col gap-1 py-2.5'
const GRID = 'grid grid-cols-1 gap-x-8 md:grid-cols-2'
const BODY = 'px-5 py-3'

// 枠の識別子。設置タグに直接書く値なので、サーバ側と同じ形だけ通す
const PLACEMENT_KEY_RE = /^[a-z0-9][a-z0-9_-]{0,63}$/

/** 枠の見た目。backend/routers/ugc_display.py の DEFAULT_DESIGN と対応する */
export interface PlacementDesign {
  columns_pc: number | string
  columns_sp: number | string
  image_ratio: 'square' | 'portrait' | 'landscape'
  radius: number | string
  card_bg: string
  text_color: string
  accent_color: string
  border: boolean
  shadow: boolean
  show_author: boolean
  show_date: boolean
  show_body: boolean
  show_verified: boolean
  show_photos: boolean
}

/** 編集フォームが持つ状態 */
interface RuleForm {
  rule_name: string
  placement_key: string
  content_type: 'review' | 'ugc' | 'mixed'
  template_type: string
  priority: number | string
  is_active: boolean
  // 2026-09-29 追加：出し分けの条件と、出す投稿の絞り込み。
  // API（ugc_display._matches_conditions / ugc_filter）は対応していたが、画面から入力できなかった
  cond_utm_source: string // カンマ区切り
  cond_device: '' | 'mobile' | 'desktop'
  cond_page_pattern: string
  cond_product_page_only: boolean
  filter_min_score: string
  expand_mode: 'inline' | 'overlay'
  default_sort: string
  show_sort: boolean
  show_filter: boolean
  show_summary: boolean
  initial_limit: string
  page_size: string
  filter_min_items: string
  design: PlacementDesign
}

const DEFAULT_DESIGN: PlacementDesign = {
  columns_pc: 4,
  columns_sp: 2,
  image_ratio: 'square',
  radius: 10,
  card_bg: '#ffffff',
  text_color: '#333333',
  accent_color: '#f59e0b',
  border: true,
  shadow: true,
  show_author: true,
  show_date: true,
  show_body: true,
  show_verified: true,
  show_photos: true,
}

const EMPTY_FORM: RuleForm = {
  rule_name: '',
  placement_key: '',
  content_type: 'review',
  template_type: 'feed',
  priority: 0,
  is_active: true,
  cond_utm_source: '',
  cond_device: '',
  cond_page_pattern: '',
  cond_product_page_only: false,
  filter_min_score: '',
  expand_mode: 'inline',
  default_sort: 'score',
  show_sort: true,
  show_filter: true,
  show_summary: true,
  initial_limit: '5',
  page_size: '10',
  filter_min_items: '10',
  design: { ...DEFAULT_DESIGN },
}

const RATIO_CSS: Record<string, string> = { square: '1', portrait: '3 / 4', landscape: '4 / 3' }

/** カードに出す要素の切り替え。design のうち真偽値の項目だけを並べる */
const CARD_ELEMENT_TOGGLES: [
  'show_author' | 'show_date' | 'show_body' | 'show_verified' | 'show_photos',
  string,
][] = [
  ['show_author', '投稿者名'],
  ['show_date', '投稿日'],
  ['show_body', '本文'],
  ['show_verified', '購入確認済みバッジ'],
  ['show_photos', '添付写真'],
]

/** 枠の種類の説明と、表示イメージのイラスト */
const CONTENT_TYPES = [
  {
    value: 'review',
    label: 'レビューのみ',
    desc: '★評価つきの購入者レビューだけを出します。並び替えと★での絞り込みが使えます。',
    illust: (
      <svg viewBox='0 0 120 72' className='h-[72px] w-[120px]' aria-hidden='true'>
        <rect x='0' y='0' width='120' height='72' rx='4' fill='currentColor' opacity='0.04' />
        {[0, 24, 48].map(y => (
          <g key={y} transform={`translate(8 ${y + 6})`}>
            <rect width='104' height='18' rx='3' fill='currentColor' opacity='0.08' />
            {[0, 1, 2, 3, 4].map(i => (
              <polygon
                key={i}
                points='3,0 4,2.2 6.4,2.4 4.6,4 5.2,6.4 3,5.1 0.8,6.4 1.4,4 -0.4,2.4 2,2.2'
                transform={`translate(${5 + i * 9} 4)`}
                fill='#f59e0b'
              />
            ))}
            <rect x='5' y='12' width='72' height='3' rx='1.5' fill='currentColor' opacity='0.25' />
          </g>
        ))}
      </svg>
    ),
  },
  {
    value: 'ugc',
    label: 'レビュー以外',
    desc: 'Instagram投稿やスタッフ投稿など、写真中心の投稿を出します。★は付いていないので評価の並び替え・絞り込みは出ません。',
    illust: (
      <svg viewBox='0 0 120 72' className='h-[72px] w-[120px]' aria-hidden='true'>
        <rect x='0' y='0' width='120' height='72' rx='4' fill='currentColor' opacity='0.04' />
        {[0, 1, 2, 3].map(i => (
          <g key={i} transform={`translate(${8 + i * 27} 12)`}>
            <rect width='24' height='24' rx='3' fill='currentColor' opacity='0.18' />
            <circle cx='8' cy='8' r='3' fill='currentColor' opacity='0.35' />
            <path d='M2 22 L9 13 L15 19 L19 15 L22 22 Z' fill='currentColor' opacity='0.35' />
            <rect y='29' width='20' height='3' rx='1.5' fill='currentColor' opacity='0.22' />
          </g>
        ))}
      </svg>
    ),
  },
  {
    value: 'mixed',
    label: '混在',
    desc: 'レビューと写真投稿をまとめて出します。★の無い投稿が混ざるため、★での絞り込みは出しません。',
    illust: (
      <svg viewBox='0 0 120 72' className='h-[72px] w-[120px]' aria-hidden='true'>
        <rect x='0' y='0' width='120' height='72' rx='4' fill='currentColor' opacity='0.04' />
        <g transform='translate(8 12)'>
          <rect width='24' height='24' rx='3' fill='currentColor' opacity='0.18' />
          <path d='M2 22 L9 13 L15 19 L19 15 L22 22 Z' fill='currentColor' opacity='0.35' />
        </g>
        <g transform='translate(38 12)'>
          <rect width='74' height='24' rx='3' fill='currentColor' opacity='0.08' />
          {[0, 1, 2, 3, 4].map(i => (
            <polygon
              key={i}
              points='3,0 4,2.2 6.4,2.4 4.6,4 5.2,6.4 3,5.1 0.8,6.4 1.4,4 -0.4,2.4 2,2.2'
              transform={`translate(${5 + i * 9} 5)`}
              fill='#f59e0b'
            />
          ))}
          <rect x='5' y='15' width='56' height='3' rx='1.5' fill='currentColor' opacity='0.25' />
        </g>
        <g transform='translate(8 44)'>
          <rect width='104' height='18' rx='3' fill='currentColor' opacity='0.08' />
          <rect x='5' y='7' width='72' height='3' rx='1.5' fill='currentColor' opacity='0.25' />
        </g>
      </svg>
    ),
  },
]

/** レイアウトの選択肢。テンプレート種別（template_type）に保存する */
const LAYOUTS = [
  { value: 'feed', label: '縦に並べる（フィード）', hint: 'レビュー向け。1件ずつ縦に積みます' },
  { value: 'grid', label: '格子（グリッド）', hint: '写真中心の投稿向け' },
  { value: 'carousel', label: '横スクロール（カルーセル）', hint: '省スペースで見せたいとき' },
]

const SAMPLE_REVIEWS = [
  { name: '山田 太郎', rating: 5, title: '想像以上でした', body: '肌なじみが良く、朝の支度が短くなりました。次も購入します。', verified: true, date: '2026/8/12' },
  { name: '鈴木 花子', rating: 4, title: 'コスパが良い', body: '価格のわりに量が多く、家族で使っています。', verified: false, date: '2026/8/9' },
  { name: '佐藤 みなみ', rating: 5, title: 'リピートです', body: '香りが控えめで使いやすいです。', verified: true, date: '2026/8/2' },
]

const SAMPLE_PHOTOS = ['#c9d6e4', '#e4d5c9', '#d3e4c9', '#e4c9dc']

/** ラベル・入力・補足を縦に積む1項目 */
function Field({ label, hint, children }: { label: ReactNode; hint?: ReactNode; children: ReactNode }) {
  return (
    <div className={ROW}>
      <label className={LABEL}>{label}</label>
      {children}
      {hint && <p className={`${HINT} m-0`}>{hint}</p>}
    </div>
  )
}

function ColorField({
  label,
  value,
  onChange,
  hint,
}: {
  label: string
  value: string
  onChange(v: string): void
  hint?: ReactNode
}) {
  return (
    <Field label={label} hint={hint}>
      <div className='flex items-center gap-2'>
        <input
          type='color'
          value={value}
          onChange={e => onChange(e.target.value)}
          className='border-border-input h-8 w-10 cursor-pointer rounded-lg border bg-surface p-1'
        />
        <input value={value} onChange={e => onChange(e.target.value)} className={`${INPUT} w-28 font-mono`} />
      </div>
    </Field>
  )
}

function Stars({ rating, color }: { rating: number; color: string }) {
  return <span style={{ color, letterSpacing: 1 }}>{'★'.repeat(rating)}{'☆'.repeat(5 - rating)}</span>
}

/** 設置サイトでの見え方。埋め込みJSと同じ変数・同じ組み立てで描く */
function Preview({ form, device }: { form: RuleForm; device: 'pc' | 'sp' }) {
  const d = form.design
  const cols = device === 'sp' ? Number(d.columns_sp) || 2 : Number(d.columns_pc) || 4
  const cardStyle = {
    background: d.card_bg,
    color: d.text_color,
    borderRadius: `${Number(d.radius) || 0}px`,
    border: d.border ? '1px solid rgba(0,0,0,0.08)' : 'none',
    boxShadow: d.shadow ? '0 1px 4px rgba(0,0,0,0.06)' : 'none',
  }
  const isReview = form.content_type === 'review'
  const layout = form.template_type

  const reviewCards = SAMPLE_REVIEWS.slice(0, 2).map((r, i) => (
    <div key={i} style={{ ...cardStyle, padding: '12px 14px' }}>
      <div className='mb-1.5 flex flex-wrap items-center gap-2'>
        <Stars rating={r.rating} color={d.accent_color} />
        {d.show_author && <span className='text-[12px] font-semibold'>{r.name}</span>}
        {d.show_verified && r.verified && (
          <span className='rounded-lg px-1.5 py-0.5 text-[9px] font-semibold' style={{ background: 'rgba(39,174,96,0.12)', color: '#1a6e3f' }}>
            購入確認済み
          </span>
        )}
        {d.show_date && <span className='ml-auto text-[10px] opacity-60'>{r.date}</span>}
      </div>
      <div className='text-[12px] font-bold'>{r.title}</div>
      {d.show_body && <div className='mt-0.5 text-[12px] leading-relaxed opacity-90'>{r.body}</div>}
      {d.show_photos && (
        <div className='mt-2 flex gap-1.5'>
          {SAMPLE_PHOTOS.slice(0, 2).map(c => (
            <div key={c} style={{ background: c, width: 48, height: 48, borderRadius: 6 }} />
          ))}
        </div>
      )}
    </div>
  ))

  const photoCards = SAMPLE_PHOTOS.map((c, i) => (
    <div key={i} style={{ ...cardStyle, overflow: 'hidden' }}>
      <div style={{ background: c, width: '100%', aspectRatio: RATIO_CSS[d.image_ratio] || '1' }} />
      <div style={{ padding: '6px 8px' }}>
        {d.show_author && <div className='text-[10px] opacity-60'>@sample_user</div>}
        {d.show_body && <div className='mt-0.5 line-clamp-2 text-[11px]'>お気に入りの一枚です。使い心地もよくて満足しています。</div>}
      </div>
    </div>
  ))

  let inner
  if (isReview || layout === 'feed') {
    inner = <div className='flex flex-col gap-2.5'>{reviewCards}</div>
  } else if (layout === 'carousel') {
    inner = (
      <div className='flex gap-2.5 overflow-x-auto pb-1.5'>
        {photoCards.map((el, i) => (
          <div key={i} style={{ minWidth: `calc((100% - ${(cols - 1) * 10}px) / ${cols})`, flexShrink: 0 }}>{el}</div>
        ))}
      </div>
    )
  } else {
    inner = (
      <div className='grid gap-2.5' style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
        {photoCards}
      </div>
    )
  }

  return (
    <div
      className='mx-auto'
      style={{
        width: device === 'sp' ? 360 : '100%',
        maxWidth: device === 'sp' ? 360 : 640,
        fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
      }}
    >
      {/* 評価サマリー */}
      {isReview && form.show_summary && (
        <div className='mb-2 flex items-baseline gap-2'>
          <span className='text-[22px] leading-none font-bold' style={{ color: d.text_color }}>4.8</span>
          <Stars rating={5} color={d.accent_color} />
          <span className='text-[12px] opacity-70' style={{ color: d.text_color }}>128件のレビュー</span>
        </div>
      )}

      {/* 絞り込み・並び替え */}
      {isReview && (form.show_filter || form.show_sort) && (
        <div className='mb-3 flex flex-col gap-2'>
          {form.show_filter && (
            <div className='flex flex-wrap gap-1.5'>
              {['★5 (98)', '★4 (21)', '★3 (6)', '写真あり (34)'].map((t, i) => (
                <span
                  key={t}
                  className='rounded-full border px-2.5 py-1 text-[11px]'
                  style={i === 0
                    ? { background: d.text_color, color: d.card_bg, borderColor: d.text_color }
                    : { borderColor: 'rgba(0,0,0,0.15)', color: d.text_color, background: d.card_bg }}
                >
                  {t}
                </span>
              ))}
            </div>
          )}
          {form.show_sort && (
            <div className='flex items-center gap-1.5'>
              <span className='text-[10px] opacity-60' style={{ color: d.text_color }}>並び替え</span>
              <span className='rounded-md border px-2 py-1 text-[11px]' style={{ borderColor: 'rgba(0,0,0,0.15)', color: d.text_color, background: d.card_bg }}>
                おすすめ順 ▾
              </span>
            </div>
          )}
        </div>
      )}

      {inner}

      {/* 展開ボタン */}
      <button
        type='button'
        className='mt-3 w-full rounded-lg border py-2.5 text-[13px] font-semibold'
        style={{ borderColor: d.text_color, color: d.text_color, background: d.card_bg }}
      >
        {form.expand_mode === 'overlay'
          ? (isReview ? 'すべてのレビューを見る（128件）' : 'すべて見る（128件）')
          : 'もっと見る（残り123件）'}
      </button>
    </div>
  )
}

export default function UGCDisplayRuleEdit() {
  const { siteId, ruleId } = useParams()
  const navigate = useNavigate()
  const isEdit = ruleId != null

  const [form, setForm] = useState<RuleForm>(EMPTY_FORM)
  const [existingKeys, setExistingKeys] = useState<string[]>([])
  const [device, setDevice] = useState<'pc' | 'sp'>('pc')
  const [loading, setLoading] = useState(isEdit)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [copied, setCopied] = useState(false)

  const set = (patch: Partial<RuleForm>) => setForm(prev => ({ ...prev, ...patch }))
  const setDesign = (patch: Partial<PlacementDesign>) =>
    setForm(prev => ({ ...prev, design: { ...prev.design, ...patch } }))

  useEffect(() => {
    setLoading(true)
    fetchRest<{ rules?: UGCDisplayRule[] }>(`/api/sites/${siteId}/ugc/display-rules`)
      .then(d => {
        const rules: UGCDisplayRule[] = d.rules || []
        setExistingKeys([
          ...new Set(rules.map(r => r.placement_key).filter((k): k is string => !!k)),
        ])
        if (isEdit) {
          const rule = rules.find(r => String(r.id) === String(ruleId))
          if (rule) setForm(toForm(rule))
          else setError('この枠は見つかりませんでした。削除された可能性があります。')
        }
      })
      .catch(() => setError('読み込みに失敗しました'))
      .finally(() => setLoading(false))
  }, [siteId, ruleId])

  const handleSave = () => {
    const key = form.placement_key.trim()
    if (key && !PLACEMENT_KEY_RE.test(key)) {
      setError('枠の識別子は半角小文字英数字・ハイフン・アンダースコアで、先頭は英数字（64文字以内）にしてください')
      return
    }
    setError('')
    setSaving(true)

    const url = isEdit
      ? `/api/sites/${siteId}/ugc/display-rules/${ruleId}`
      : `/api/sites/${siteId}/ugc/display-rules`

    fetchRest(url, { method: isEdit ? 'PUT' : 'POST', body: JSON.stringify(toPayload(form)) })
      .then(() => navigate(ugcDisplayRulesPath(Number(siteId))))
      // fetchRest は失敗時に Response を throw する。サーバが返す理由を拾う
      .catch(async e => {
        const body = e instanceof Response ? await e.json().catch(() => ({})) : {}
        setError(body.detail || '保存に失敗しました')
      })
      .finally(() => setSaving(false))
  }

  const typedKey = form.placement_key.trim()
  const keyIsNew = typedKey && !existingKeys.includes(typedKey)
  const keyValid = typedKey && PLACEMENT_KEY_RE.test(typedKey)
  const isReviewFrame = form.content_type === 'review'
  const d = form.design

  const copyTag = () => {
    navigator.clipboard.writeText(embedTag(typedKey))
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <Main type={MainLayoutType.FULL}>
      <PageTitle
        title={isEdit ? '枠を編集' : '枠を追加'}
        description='ECサイトのどこに何を出すか、どう見せるかを設定します'
        rightNode={
          <Button color={ButtonColor.SECONDARY} onClick={() => navigate(ugcDisplayRulesPath(Number(siteId)))}>
            一覧へ戻る
          </Button>
        }
      />

      {loading ? (
        <div className='text-secondary py-16 text-center text-sm'>読み込み中...</div>
      ) : (
        <div className='grid grid-cols-1 gap-4 xl:grid-cols-[1fr_460px]'>
          {/* 設定 */}
          <div className='flex flex-col gap-4'>
            <Area>
              <Heading subText='どのタグから呼び出す枠かを決めます'>基本</Heading>
              <div className={BODY}>
                <div className={GRID}>
                  <Field label='枠の名前'>
                    <input
                      value={form.rule_name}
                      onChange={e => set({ rule_name: e.target.value })}
                      placeholder='商品ページ レビュー枠'
                      className={`${INPUT} ${W_LONG}`}
                    />
                  </Field>

                  <Field
                    label='枠の識別子'
                    hint={
                      <>
                        設置タグの <code>data-placement</code> に書く値です。
                        空にすると従来の設置タグ（<code>id=&quot;ugc-reviews&quot;</code> 等）向けの設定になります。
                        {keyIsNew && <span className='text-attention'>／新しい枠になります</span>}
                      </>
                    }
                  >
                    <input
                      value={form.placement_key}
                      onChange={e => set({ placement_key: e.target.value })}
                      placeholder='review-main'
                      list='w2ugc-placement-keys'
                      className={`${INPUT} ${W_MID} font-mono`}
                    />
                    <datalist id='w2ugc-placement-keys'>
                      {existingKeys.map(k => <option key={k} value={k} />)}
                    </datalist>
                  </Field>

                  <Field label='優先度' hint='同じ識別子の枠を複数作ったとき、下の「表示する条件」に合うもののうち、数字の大きいものが使われます。'>
                    <input type='number' value={form.priority} onChange={e => set({ priority: e.target.value })} className={`${INPUT} ${W_NUM}`} />
                  </Field>

                  <Field label='状態'>
                    <label className='text-primary flex h-8 items-center gap-2 text-[13px]'>
                      <input type='checkbox' checked={form.is_active} onChange={e => set({ is_active: e.target.checked })} />
                      有効にする
                    </label>
                  </Field>
                </div>

                {keyValid && (
                  <div className='bg-surface-secondary mt-2 rounded-lg p-3'>
                    <div className='mb-2 flex items-center justify-between'>
                      <span className='text-secondary text-[11px] font-semibold'>この枠の設置タグ</span>
                      <Button color={ButtonColor.SECONDARY} onClick={copyTag}>
                        {copied ? '✅ コピーしました' : 'コピー'}
                      </Button>
                    </div>
                    <pre className='text-primary m-0 overflow-auto font-mono text-[12px] whitespace-pre-wrap'>{embedTag(typedKey)}</pre>
                    <p className={`${HINT} mt-1 mb-0`}>
                      設置タグの値とこの識別子が一字でも違うと、その枠には何も表示されません。
                    </p>
                  </div>
                )}
              </div>
            </Area>

            {/* 2026-09-29 追加：流入元・デバイス別の出し分けと、出す投稿の絞り込み */}
            <Area>
              <Heading subText='空欄の条件は「問わない」です。すべて空欄なら、いつでもこの枠が使われます。同じ識別子で条件違いの枠を作ると、見ている人に合わせて中身を出し分けられます（例：Instagram 広告から来た人には写真投稿、それ以外にはレビュー）。'>
                表示する条件（出し分け）
              </Heading>
              <div className={BODY}>
                <div className={GRID}>
                  <Field
                    label='流入元（utm_source）'
                    hint={
                      <>
                        表示しているページのURLに付いた <code>utm_source</code> で判定します。複数はカンマ区切り（例：instagram, line）。
                        広告や SNS のリンク先URLに <code>?utm_source=instagram</code> のように付けておく必要があります。
                      </>
                    }
                  >
                    <input
                      value={form.cond_utm_source}
                      onChange={e => set({ cond_utm_source: e.target.value })}
                      placeholder='instagram, line'
                      className={`${INPUT} ${W_MID}`}
                    />
                  </Field>

                  <Field label='デバイス' hint='画面の幅が768px以下をスマートフォンとして判定します。'>
                    <select
                      value={form.cond_device}
                      onChange={e => set({ cond_device: e.target.value as RuleForm['cond_device'] })}
                      className={`${INPUT} ${W_SHORT}`}
                    >
                      <option value=''>問わない</option>
                      <option value='mobile'>スマートフォン</option>
                      <option value='desktop'>PC</option>
                    </select>
                  </Field>

                  <Field label='ページのURL' hint='URLにこの文字を含むページだけで表示します（例：/campaign/）。正規表現も使えます。'>
                    <input
                      value={form.cond_page_pattern}
                      onChange={e => set({ cond_page_pattern: e.target.value })}
                      placeholder='/campaign/'
                      className={`${INPUT} ${W_MID} font-mono`}
                    />
                  </Field>

                  <Field label='商品ページだけ' hint='設置タグに data-product-id があるページ（商品ページ）だけで表示します。'>
                    <label className='text-primary flex h-8 items-center gap-2 text-[13px]'>
                      <input
                        type='checkbox'
                        checked={form.cond_product_page_only}
                        onChange={e => set({ cond_product_page_only: e.target.checked })}
                      />
                      商品ページだけで表示する
                    </label>
                  </Field>
                </div>
              </div>
            </Area>

            <Area>
              <Heading subText='この枠に出す投稿を、公開中・許諾済みのものからさらに絞り込みます。'>表示する投稿の絞り込み</Heading>
              <div className={BODY}>
                <div className={GRID}>
                  <Field label='AIスコアの下限' hint='この点数未満の投稿は出しません（空欄なら絞り込まない）。AIスコアリング設定で採点した点数です。'>
                    <input
                      type='number'
                      min={0}
                      max={100}
                      value={form.filter_min_score}
                      onChange={e => set({ filter_min_score: e.target.value })}
                      placeholder='70'
                      className={`${INPUT} ${W_NUM}`}
                    />
                  </Field>
                </div>
              </div>
            </Area>

            <Area>
              <Heading subText='出す投稿の種類です。★を使えるかどうかがここで決まります'>枠の種類</Heading>
              <div className={BODY}>
                <div className='grid grid-cols-1 gap-3 py-1 md:grid-cols-3'>
                  {CONTENT_TYPES.map(ct => {
                    const on = form.content_type === ct.value
                    return (
                      <label
                        key={ct.value}
                        className={`flex cursor-pointer flex-col gap-2 rounded-xl border-2 p-3 transition-colors ${
                          on ? 'border-emphasis bg-surface-emphasis' : 'border-secondary bg-surface hover:border-border-input-hover'
                        }`}
                      >
                        <div className='flex items-center gap-2'>
                          <input
                            type='radio'
                            name='content_type'
                            checked={on}
                            onChange={() => set({
                              content_type: ct.value as RuleForm['content_type'],
                              // レビューは縦積み、写真中心は格子が既定。あとから変えられる
                              template_type: ct.value === 'ugc' ? 'grid' : form.template_type,
                            })}
                          />
                          <span className='text-primary text-[13px] font-bold'>{ct.label}</span>
                        </div>
                        <div className='text-secondary flex justify-center'>{ct.illust}</div>
                        <p className={`${HINT} m-0`}>{ct.desc}</p>
                      </label>
                    )
                  })}
                </div>
              </div>
            </Area>

            <Area>
              <Heading subText='設置サイトのCSSに左右されず、この設定どおりに表示します'>デザイン</Heading>
              <div className={BODY}>
                <div className={GRID}>
                  <Field label='レイアウト' hint={LAYOUTS.find(l => l.value === form.template_type)?.hint}>
                    <select value={form.template_type} onChange={e => set({ template_type: e.target.value })} className={`${INPUT} ${W_MID}`}>
                      {LAYOUTS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                    </select>
                  </Field>

                  <Field label='画像の形' hint='格子・横スクロールのときの画像の縦横比です。'>
                    <select
                      value={d.image_ratio}
                      onChange={e => setDesign({ image_ratio: e.target.value as PlacementDesign['image_ratio'] })}
                      className={`${INPUT} ${W_SHORT}`}
                    >
                      <option value='square'>正方形</option>
                      <option value='portrait'>縦長（3:4）</option>
                      <option value='landscape'>横長（4:3）</option>
                    </select>
                  </Field>

                  <Field label='横に並べる数（PC）' hint='格子・横スクロールのときに使います。'>
                    <input type='number' min='1' max='6' value={d.columns_pc} onChange={e => setDesign({ columns_pc: e.target.value })} className={`${INPUT} ${W_NUM}`} />
                  </Field>

                  <Field label='横に並べる数（スマートフォン）' hint='画面幅768px以下で切り替わります。'>
                    <input type='number' min='1' max='4' value={d.columns_sp} onChange={e => setDesign({ columns_sp: e.target.value })} className={`${INPUT} ${W_NUM}`} />
                  </Field>

                  <Field label='角丸（px）'>
                    <input type='number' min='0' max='24' value={d.radius} onChange={e => setDesign({ radius: e.target.value })} className={`${INPUT} ${W_NUM}`} />
                  </Field>

                  <Field label='枠線と影'>
                    <div className='flex flex-col gap-1 pt-1'>
                      <label className='text-primary flex items-center gap-2 text-[13px]'>
                        <input type='checkbox' checked={d.border} onChange={e => setDesign({ border: e.target.checked })} />
                        枠線を付ける
                      </label>
                      <label className='text-primary flex items-center gap-2 text-[13px]'>
                        <input type='checkbox' checked={d.shadow} onChange={e => setDesign({ shadow: e.target.checked })} />
                        影を付ける
                      </label>
                    </div>
                  </Field>

                  <ColorField label='カードの背景色' value={d.card_bg} onChange={v => setDesign({ card_bg: v })} />
                  <ColorField label='文字色' value={d.text_color} onChange={v => setDesign({ text_color: v })} />
                  <ColorField label='★とボタンの色' value={d.accent_color} onChange={v => setDesign({ accent_color: v })} />
                </div>

                <div className={`${ROW} border-secondary border-t`}>
                  <span className={LABEL}>カードに出す要素</span>
                  <div className='flex flex-wrap gap-x-5 gap-y-1.5 pt-1'>
                    {CARD_ELEMENT_TOGGLES.map(([key, label]) => (
                      <label key={key} className='text-primary flex items-center gap-2 text-[13px]'>
                        <input
                          type='checkbox'
                          checked={d[key]}
                          onChange={e => setDesign({ [key]: e.target.checked })}
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </Area>

            <Area>
              <Heading subText='ECサイトの閲覧者が操作する部分です'>閲覧者向けの表示</Heading>
              <div className={BODY}>
                <div className={GRID}>
                  <Field
                    label='すべて表示の方式'
                    hint='ページ内は「もっと見る」で下に足し、モーダルは「すべてのレビューを見る」で開きます。件数が多く、商品ページを長くしたくないときはモーダル。'
                  >
                    <select
                      value={form.expand_mode}
                      onChange={e => set({ expand_mode: e.target.value as RuleForm['expand_mode'] })}
                      className={`${INPUT} ${W_MID}`}
                    >
                      <option value='inline'>ページ内に追記</option>
                      <option value='overlay'>モーダルを開く</option>
                    </select>
                  </Field>

                  <Field label='既定の並び順'>
                    <select value={form.default_sort} onChange={e => set({ default_sort: e.target.value })} className={`${INPUT} ${W_SHORT}`}>
                      <option value='score'>おすすめ順</option>
                      <option value='newest'>新着順</option>
                      <option value='rating_desc'>評価の高い順</option>
                      <option value='rating_asc'>評価の低い順</option>
                    </select>
                  </Field>

                  <Field label='初期表示件数' hint='設置タグに data-limit があるときはそちらが優先されます。'>
                    <input type='number' value={form.initial_limit} onChange={e => set({ initial_limit: e.target.value })} className={`${INPUT} ${W_NUM}`} />
                  </Field>

                  <Field label='「もっと見る」1回あたりの件数'>
                    <input type='number' value={form.page_size} onChange={e => set({ page_size: e.target.value })} className={`${INPUT} ${W_NUM}`} />
                  </Field>
                </div>

                <div className={`${ROW} border-secondary border-t`}>
                  <span className={LABEL}>出す要素</span>
                  <div className='flex flex-col gap-1.5 pt-1'>
                    <label className='text-primary flex items-center gap-2 text-[13px]'>
                      <input type='checkbox' checked={form.show_summary} onChange={e => set({ show_summary: e.target.checked })} />
                      評価サマリー（平均★と件数）
                    </label>
                    <label className='text-primary flex items-center gap-2 text-[13px]'>
                      <input type='checkbox' checked={form.show_sort} onChange={e => set({ show_sort: e.target.checked })} />
                      並び替え
                    </label>
                    <label className='text-primary flex items-center gap-2 text-[13px]'>
                      <input type='checkbox' checked={form.show_filter} onChange={e => set({ show_filter: e.target.checked })} />
                      絞り込み（★・写真あり）
                    </label>

                    {/* 出す/出さないの判断は件数から自動でしている。ここは線引きの位置だけ */}
                    {form.show_filter && (
                      <div className='border-secondary mt-1 ml-6 border-l pl-3'>
                        <label className='text-secondary flex items-center gap-2 text-[12px]'>
                          対象が
                          <input type='number' value={form.filter_min_items} onChange={e => set({ filter_min_items: e.target.value })} className={`${INPUT} w-16`} />
                          件未満のときは自動で隠す
                        </label>
                        <p className={`${HINT} mt-1 mb-0`}>
                          数件しかない枠で絞り込みを出しても、押して結果が変わらず邪魔になるだけのためです。通常は既定のままで構いません。
                        </p>
                      </div>
                    )}
                  </div>
                  {!isReviewFrame && (
                    <p className={`${HINT} m-0`}>
                      ★の並び替え・絞り込みは「レビューのみ」の枠にだけ出ます。この枠では反映されません。
                    </p>
                  )}
                </div>
              </div>
            </Area>

            {error && <p className='text-critical m-0 text-[12px]'>{error}</p>}

            <div className='flex gap-2'>
              <Button color={ButtonColor.PRIMARY} size='large' disabled={!form.rule_name || saving} onClick={handleSave}>
                {saving ? '保存中...' : isEdit ? '更新' : '作成'}
              </Button>
              <Button color={ButtonColor.SECONDARY} size='large' onClick={() => navigate(ugcDisplayRulesPath(Number(siteId)))}>
                キャンセル
              </Button>
            </div>
          </div>

          {/* プレビュー */}
          <div className='xl:sticky xl:top-4 xl:self-start'>
            <Area>
              <Heading
                subText='設定を変えるとその場で反映されます。中身はサンプルです'
                rightNode={
                  <div className='flex shrink-0 gap-1'>
                    {[['pc', 'PC'], ['sp', 'スマートフォン']].map(([v, label]) => (
                      <button
                        key={v}
                        type='button'
                        onClick={() => setDevice(v as 'pc' | 'sp')}
                        className={`rounded-md px-2.5 py-1 text-[11px] font-semibold whitespace-nowrap ${
                          device === v ? 'bg-fill-brand text-on-bg-fill' : 'bg-surface-secondary text-secondary'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                }
              >
                プレビュー
              </Heading>
              <div className='border-secondary bg-surface-secondary border-t px-4 py-5'>
                <Preview form={form} device={device} />
              </div>
            </Area>
          </div>
        </div>
      )}
    </Main>
  )
}

/** 表示ルールの conditions / ugc_filter をフォームの項目に開く */
function condFields(rule: UGCDisplayRule) {
  const c = (rule.conditions || {}) as Record<string, unknown>
  const f = (rule.ugc_filter || {}) as Record<string, unknown>
  const utm = c.utm_source
  const device = c.device === 'mobile' || c.device === 'desktop' ? c.device : ''
  return {
    cond_utm_source: Array.isArray(utm) ? utm.join(', ') : typeof utm === 'string' ? utm : '',
    cond_device: device as RuleForm['cond_device'],
    cond_page_pattern: typeof c.page_pattern === 'string' ? c.page_pattern : '',
    cond_product_page_only: !!c.product_page_only,
    filter_min_score: typeof f.min_score === 'number' ? String(f.min_score) : ''
  }
}

/** 表示ルールを編集フォームの形に開く */
function toForm(rule: UGCDisplayRule): RuleForm {
  const u = rule.ui_config || {}
  const asNum = (v: unknown, fallback: string) => (v === 0 || v ? String(v) : fallback)
  return {
    rule_name: rule.rule_name || '',
    placement_key: rule.placement_key || '',
    content_type: rule.content_type || 'mixed',
    template_type: rule.template_type || 'feed',
    priority: rule.priority || 0,
    is_active: rule.is_active !== false,
    ...condFields(rule),
    expand_mode: u.expand_mode === 'overlay' ? 'overlay' : 'inline',
    default_sort: u.default_sort || 'score',
    show_sort: u.show_sort !== false,
    show_filter: u.show_filter !== false,
    show_summary: u.show_summary !== false,
    initial_limit: asNum(u.initial_limit, '5'),
    page_size: asNum(u.page_size, '10'),
    filter_min_items: asNum(u.filter_min_items, '10'),
    design: { ...DEFAULT_DESIGN, ...((u.design as Partial<PlacementDesign>) || {}) },
  }
}

/** フォームを API のリクエストボディにする */
function toPayload(form: RuleForm) {
  const d = form.design
  const ui_config = {
    expand_mode: form.expand_mode,
    default_sort: form.default_sort,
    show_sort: !!form.show_sort,
    show_filter: !!form.show_filter,
    show_summary: !!form.show_summary,
    initial_limit: Number(form.initial_limit) || 5,
    page_size: Number(form.page_size) || 10,
    filter_min_items: Number(form.filter_min_items) || 0,
    design: {
      ...d,
      columns_pc: Number(d.columns_pc) || 4,
      columns_sp: Number(d.columns_sp) || 2,
      radius: Number(d.radius) || 0,
    },
  }

  return {
    rule_name: form.rule_name,
    // 空文字は「枠なし（従来の設置タグ向け）」の意味でサーバへ渡す
    placement_key: form.placement_key.trim(),
    content_type: form.content_type,
    template_type: form.template_type,
    priority: Number(form.priority) || 0,
    is_active: !!form.is_active,
    conditions: toConditions(form),
    ugc_filter: toFilter(form),
    ui_config,
  }
}

/** 空欄の条件は送らない（キーがあるとサーバは「その値のときだけ」と解釈する） */
function toConditions(form: RuleForm) {
  const c: Record<string, unknown> = {}
  const utm = form.cond_utm_source.split(',').map(v => v.trim()).filter(Boolean)
  if (utm.length) c.utm_source = utm
  if (form.cond_device) c.device = form.cond_device
  if (form.cond_page_pattern.trim()) c.page_pattern = form.cond_page_pattern.trim()
  if (form.cond_product_page_only) c.product_page_only = true
  return c
}

function toFilter(form: RuleForm) {
  const f: Record<string, unknown> = {}
  const min = Number(form.filter_min_score)
  if (form.filter_min_score.trim() !== '' && !Number.isNaN(min) && min > 0) f.min_score = min
  return f
}

function embedTag(placementKey: string) {
  return `<div class="w2ugc" data-placement="${placementKey}" data-product-id="{商品ID}"></div>`
}
